import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Leaf, Bird, Fish, Shell, Camera, Search, X, ChevronDown, ChevronUp,
  Sparkles, Loader2, TreePine, Flower2, Waves, Info, AlertTriangle,
  Plus, Trash2, Edit, Save, Image as ImageIcon, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Categorie disponibili
const CATEGORIES = [
  { id: 'tartarughe', label: 'Tartarughe', icon: '🐢' },
  { id: 'mammiferi', label: 'Mammiferi Marini', icon: '🐬' },
  { id: 'pesci', label: 'Pesci', icon: '🐟' },
  { id: 'uccelli', label: 'Uccelli', icon: '🐦' },
  { id: 'cefalopodi', label: 'Cefalopodi', icon: '🐙' },
  { id: 'decapoda', label: 'Decapoda', icon: '🦀' },
  { id: 'alghe', label: 'Alghe', icon: '🌊' },
  { id: 'piante_acquatiche', label: 'Piante Acquatiche', icon: '🌱' },
  { id: 'piante', label: 'Piante Zone Costiere e Intertidali', icon: '🌿' },
  { id: 'fiori', label: 'Fiori', icon: '🌸' },
  { id: 'altro', label: 'Altro', icon: '🔬' }
];

const FloraFaunaSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [showNestingWarning, setShowNestingWarning] = useState(true);
  
  // AI Identifier
  const [showAiIdentifier, setShowAiIdentifier] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  
  // Admin form
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    common_name: '',
    category: 'pesci',
    image_url: '',
    description: '',
    habitat: '',
    curiosity: '',
    protection: '',
    emergency: ''
  });

  const isAdmin = user?.email === 'massimo_marchiori@yahoo.it';

  // Fetch specie dal database
  const fetchSpecies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/species`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpecies(response.data);
    } catch (error) {
      console.error('Error fetching species:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchSpecies();
  }, [token, fetchSpecies]);

  // Filtra specie per categoria
  const filteredSpecies = selectedCategory === 'all' 
    ? species 
    : species.filter(s => s.category === selectedCategory);

  // Gestione foto per identificazione IA
  const handlePhotoSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
      setAiResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const analyzeSpecies = useCallback(async () => {
    if (!photoPreview || !token) return;
    
    setAnalyzing(true);
    try {
      const base64 = photoPreview.split(',')[1];
      
      const response = await axios.post(
        `${BACKEND_URL}/api/identify-species`,
        { image_base64: base64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiResult(response.data);
      toast.success('Specie identificata!');
    } catch (error) {
      console.error('AI error:', error);
      toast.error('Errore nell\'identificazione. Riprova.');
    } finally {
      setAnalyzing(false);
    }
  }, [photoPreview, token]);

  const resetAiIdentifier = () => {
    setPhotoPreview(null);
    setAiResult(null);
    setShowAiIdentifier(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // Admin: Salva specie
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.common_name || !form.description) {
      toast.error('Compila nome, nome comune e descrizione');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingSpecies) {
        await axios.put(
          `${BACKEND_URL}/api/species/${editingSpecies.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Specie aggiornata!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/species`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Specie creata!');
      }
      
      setShowAdminForm(false);
      setEditingSpecies(null);
      setForm({
        name: '', common_name: '', category: 'pesci', image_url: '',
        description: '', habitat: '', curiosity: '', protection: '', emergency: ''
      });
      fetchSpecies();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (spec) => {
    setEditingSpecies(spec);
    setForm({
      name: spec.name || '',
      common_name: spec.common_name || '',
      category: spec.category || 'pesci',
      image_url: spec.image_url || '',
      description: spec.description || '',
      habitat: spec.habitat || '',
      curiosity: spec.curiosity || '',
      protection: spec.protection || '',
      emergency: spec.emergency || ''
    });
    setShowAdminForm(true);
  };

  const handleDelete = async (speciesId) => {
    if (!window.confirm('Eliminare questa specie?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/species/${speciesId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Specie eliminata');
      fetchSpecies();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[7];

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Popup Avvertimento Nidificazioni */}
      {showNestingWarning && (
        <div key={`nesting-warning-${language}`} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-amber-500/30 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5">
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400">
                  <TranslatedText text="Custodi in Allerta: Rispetta i Ritmi del Mare" />
                </h3>
              </div>
              
              <p className="text-slate-300 text-sm mb-4">
                <TranslatedText text="Far parte della community di Respiro Blu significa camminare con passo leggero. Durante le tue attività di monitoraggio e raccolta, ricorda che le nostre spiagge sono culle fragili. In determinati periodi dell'anno, il Mediterraneo diventa il teatro delle nidificazioni." />
              </p>
              
              <p className="text-white font-semibold text-sm mb-3">
                <TranslatedText text="Le date da ricordare:" />
              </p>
              
              <div className="mb-3 p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                <p className="text-teal-400 font-bold text-sm mb-2">🐢 <TranslatedText text="Tartarughe Marine" /></p>
                <ul className="text-slate-300 text-xs space-y-1 ml-4">
                  <li><strong><TranslatedText text="Nidificazione:" /></strong> <TranslatedText text="Fine maggio - metà agosto" /></li>
                  <li><strong><TranslatedText text="Schiusa:" /></strong> <TranslatedText text="Fine luglio - fine settembre" /></li>
                  <li><strong><TranslatedText text="Cosa fare:" /></strong> <TranslatedText text="Se vedi tracce cingolate sulla sabbia, usa il tasto SOS per avvisare la Guardia Costiera." /></li>
                </ul>
              </div>
              
              <div className="mb-3 p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg">
                <p className="text-sky-400 font-bold text-sm mb-2">🐦 <TranslatedText text="Fratino e Corriere piccolo" /></p>
                <ul className="text-slate-300 text-xs space-y-1 ml-4">
                  <li><strong><TranslatedText text="Nidificazione:" /></strong> <TranslatedText text="Marzo - luglio" /></li>
                  <li><strong><TranslatedText text="Cosa fare:" /></strong> <TranslatedText text="Cammina vicino alla battigia, i nidi sono quasi invisibili sulla sabbia." /></li>
                </ul>
              </div>
              
              <div className="mb-4 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                <p className="text-violet-400 font-bold text-sm mb-2">🐦 <TranslatedText text="Fraticelli e Sterne" /></p>
                <ul className="text-slate-300 text-xs space-y-1 ml-4">
                  <li><strong><TranslatedText text="Nidificazione:" /></strong> <TranslatedText text="Maggio - giugno" /></li>
                  <li><strong><TranslatedText text="Cosa fare:" /></strong> <TranslatedText text="Se gli uccelli si alzano gridando, sei troppo vicino. Allontanati lentamente." /></li>
                </ul>
              </div>
              
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                <p className="text-emerald-400 font-bold text-sm mb-2">🎯 <TranslatedText text="La tua missione:" /></p>
                <p className="text-slate-300 text-xs italic">
                  <TranslatedText text="Raccogli la plastica, ma lascia intatto il silenzio e lo spazio per la vita." />
                </p>
              </div>
              
              <button
                onClick={() => setShowNestingWarning(false)}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-xl"
              >
                <TranslatedText text="Ho capito, procedo con rispetto" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <Leaf className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed'}}>
          FLORA E FAUNA
        </h2>
        <p className="text-slate-400 text-sm">
          Scopri le specie che puoi incontrare durante le pulizie
        </p>
      </div>

      {/* Pulsante Identifica con IA */}
      <button
        onClick={() => setShowAiIdentifier(true)}
        className="w-full mb-4 py-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-3"
      >
        <Camera className="w-6 h-6" />
        <span>Identifica una specie con l'IA</span>
        <Sparkles className="w-5 h-5" />
      </button>

      {/* Pulsante Admin per aggiungere specie */}
      {isAdmin && (
        <button
          onClick={() => { setEditingSpecies(null); setForm({ name: '', common_name: '', category: 'pesci', image_url: '', description: '', habitat: '', curiosity: '', protection: '', emergency: '' }); setShowAdminForm(true); }}
          className="w-full mb-6 py-3 bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-500/30"
        >
          <Plus className="w-5 h-5" />
          Aggiungi nuova specie
        </button>
      )}

      {/* Modal IA Identifier */}
      {showAiIdentifier && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-violet-500/30 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Identifica Specie
              </h3>
              <button onClick={resetAiIdentifier} className="p-1 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {!photoPreview ? (
                <div className="space-y-3">
                  {/* Input nascosto che accetta sia camera che galleria */}
                  <input 
                    ref={photoInputRef}
                    type="file" 
                    accept="image/*" 
                    onChange={handlePhotoSelect} 
                    className="hidden" 
                  />
                  
                  {/* Pulsante per scattare foto */}
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.capture = 'environment';
                      input.onchange = handlePhotoSelect;
                      input.click();
                    }}
                    className="w-full py-6 border-2 border-dashed border-violet-500/50 rounded-xl text-center hover:border-violet-500 bg-violet-500/5"
                  >
                    <Camera className="w-10 h-10 mx-auto mb-2 text-violet-400" />
                    <p className="text-violet-400 font-medium">Scatta una foto</p>
                  </button>
                  
                  {/* Pulsante per caricare da galleria */}
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full py-6 border-2 border-dashed border-cyan-500/50 rounded-xl text-center hover:border-cyan-500 bg-cyan-500/5"
                  >
                    <Upload className="w-10 h-10 mx-auto mb-2 text-cyan-400" />
                    <p className="text-cyan-400 font-medium">Carica dalla galleria</p>
                  </button>
                </div>
              ) : (
                <>
                  <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-xl mb-4" />
                  
                  {!aiResult ? (
                    <div className="space-y-2">
                      <button
                        onClick={analyzeSpecies}
                        disabled={analyzing}
                        className="w-full py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
                      >
                        {analyzing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Analisi in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            Identifica
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => { setPhotoPreview(null); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                        className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl text-sm"
                      >
                        Cambia foto
                      </button>
                    </div>
                  ) : (
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h4 className="text-xl font-bold text-white mb-1">{aiResult.common_name}</h4>
                      <p className="text-violet-400 italic text-sm mb-3">{aiResult.scientific_name}</p>
                      
                      <p className="text-slate-300 text-sm mb-3">{aiResult.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        {aiResult.habitat && (
                          <div className="flex items-start gap-2">
                            <span className="text-emerald-400">📍</span>
                            <span className="text-slate-400"><strong className="text-slate-300">Habitat:</strong> {aiResult.habitat}</span>
                          </div>
                        )}
                        {aiResult.curiosity && (
                          <div className="flex items-start gap-2">
                            <span className="text-amber-400">💡</span>
                            <span className="text-slate-400"><strong className="text-slate-300">Curiosità:</strong> {aiResult.curiosity}</span>
                          </div>
                        )}
                        {aiResult.protection && (
                          <div className="flex items-start gap-2">
                            <span className="text-red-400">🛡️</span>
                            <span className="text-slate-400"><strong className="text-slate-300">Protezione:</strong> {aiResult.protection}</span>
                          </div>
                        )}
                        {aiResult.emergency && (
                          <div className="mt-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 text-xs font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              SE IN DIFFICOLTÀ
                            </p>
                            <p className="text-red-300 text-xs mt-1">{aiResult.emergency}</p>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={resetAiIdentifier}
                        className="w-full mt-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                      >
                        Nuova identificazione
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Admin Form */}
      {showAdminForm && isAdmin && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {editingSpecies ? 'Modifica Specie' : 'Nuova Specie'}
              </h3>
              <button onClick={() => setShowAdminForm(false)} className="p-1 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nome scientifico *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Es: Caretta caretta"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nome comune *</label>
                  <input
                    type="text"
                    value={form.common_name}
                    onChange={(e) => setForm(p => ({ ...p, common_name: e.target.value }))}
                    placeholder="Es: Tartaruga comune"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Categoria *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">URL Immagine *</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                />
                {form.image_url && (
                  <img src={form.image_url} alt="Preview" className="mt-2 h-24 w-full object-cover rounded-lg" />
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Carica l'immagine su un servizio esterno (es: Imgur) e incolla il link
                </p>
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Descrizione *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Descrizione della specie..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Habitat</label>
                <input
                  type="text"
                  value={form.habitat}
                  onChange={(e) => setForm(p => ({ ...p, habitat: e.target.value }))}
                  placeholder="Dove vive questa specie"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Curiosità</label>
                <input
                  type="text"
                  value={form.curiosity}
                  onChange={(e) => setForm(p => ({ ...p, curiosity: e.target.value }))}
                  placeholder="Un fatto interessante"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Stato di protezione</label>
                <input
                  type="text"
                  value={form.protection}
                  onChange={(e) => setForm(p => ({ ...p, protection: e.target.value }))}
                  placeholder="Es: Specie protetta, Vulnerabile..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Cosa fare se in difficoltà</label>
                <textarea
                  value={form.emergency}
                  onChange={(e) => setForm(p => ({ ...p, emergency: e.target.value }))}
                  placeholder="Istruzioni per animali in difficoltà..."
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none"
                />
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {editingSpecies ? 'Salva modifiche' : 'Crea specie'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Filtri Categorie */}
      <div className="flex overflow-x-auto gap-2 pb-2 mb-6 scrollbar-hide">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-white/20 border border-white/50 text-white'
              : 'bg-slate-800/50 border border-slate-700 text-slate-400'
          }`}
        >
          Tutte
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-emerald-500/20 border border-emerald-500 text-emerald-400'
                : 'bg-slate-800/50 border border-slate-700 text-slate-400'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Lista Specie */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400" />
          <p className="text-slate-400 mt-2">Caricamento...</p>
        </div>
      ) : filteredSpecies.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <Leaf className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-400">Nessuna specie trovata</p>
          {isAdmin && (
            <p className="text-slate-500 text-sm mt-1">Clicca "Aggiungi nuova specie" per iniziare</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredSpecies.map((spec) => {
            const catInfo = getCategoryInfo(spec.category);
            return (
              <div
                key={spec.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600 transition-all cursor-pointer group relative"
              >
                <div onClick={() => setSelectedSpecies(spec)} className="relative h-36 overflow-hidden">
                  {spec.image_url ? (
                    <img 
                      src={spec.image_url} 
                      alt={spec.common_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-slate-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 bg-black/50 rounded-full text-xs">
                      {catInfo.icon} {catInfo.label}
                    </span>
                  </div>
                  <div className="absolute bottom-2 left-3 right-3">
                    <h3 className="text-white font-bold text-lg leading-tight">{spec.common_name}</h3>
                    <p className="text-slate-400 text-xs italic">{spec.name}</p>
                  </div>
                </div>
                
                <div className="p-3" onClick={() => setSelectedSpecies(spec)}>
                  <p className="text-slate-400 text-sm line-clamp-2"><TranslatedText text={spec.description} /></p>
                  {spec.protection && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                      🛡️ {spec.protection.substring(0, 30)}...
                    </span>
                  )}
                </div>
                
                {/* Admin actions */}
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(spec); }}
                      className="p-2 bg-black/50 hover:bg-blue-500/50 rounded-full text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(spec.id); }}
                      className="p-2 bg-black/50 hover:bg-red-500/50 rounded-full text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Dettaglio Specie */}
      {selectedSpecies && (
        <div key={`species-modal-${selectedSpecies.id}-${language}`} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              {selectedSpecies.image_url ? (
                <img 
                  src={selectedSpecies.image_url} 
                  alt={selectedSpecies.common_name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-slate-800 flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-slate-600" />
                </div>
              )}
              <button
                onClick={() => setSelectedSpecies(null)}
                className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900 to-transparent">
                <h3 className="text-2xl font-bold text-white">{selectedSpecies.common_name}</h3>
                <p className="text-slate-400 italic">{selectedSpecies.name}</p>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-slate-300"><TranslatedText text={selectedSpecies.description} /></p>
              
              <div className="space-y-3">
                {selectedSpecies.habitat && (
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <span className="text-xl">📍</span>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Habitat</p>
                      <p className="text-slate-300 text-sm"><TranslatedText text={selectedSpecies.habitat} /></p>
                    </div>
                  </div>
                )}
                
                {selectedSpecies.curiosity && (
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <span className="text-xl">💡</span>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Lo sapevi che...</p>
                      <p className="text-slate-300 text-sm"><TranslatedText text={selectedSpecies.curiosity} /></p>
                    </div>
                  </div>
                )}
                
                {selectedSpecies.protection && (
                  <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <span className="text-xl">🛡️</span>
                    <div>
                      <p className="text-xs text-amber-400 uppercase">Stato di protezione</p>
                      <p className="text-amber-300 text-sm"><TranslatedText text={selectedSpecies.protection} /></p>
                    </div>
                  </div>
                )}
                
                {selectedSpecies.emergency && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 font-medium flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      COSA FARE SE IN DIFFICOLTÀ
                    </p>
                    <p className="text-red-300 text-sm"><TranslatedText text={selectedSpecies.emergency} /></p>
                    <a 
                      href="tel:1530"
                      className="mt-3 block w-full py-2 bg-red-500 hover:bg-red-600 text-white text-center font-medium rounded-lg"
                    >
                      📞 Chiama 1530 (Guardia Costiera)
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloraFaunaSection;
